import { getApiUrl } from "../config";
import type { GetUserDto, GetUserSessionDto, GetUsersResponseDto, UpdateUserRoleDto, UpdateUserRoleResponseDto, SearchUsersResponseDto, GetUserUsageStatsDto, DeleteUserResponseDto } from "@server/routes/users/dto";

export async function fetchUser(): Promise<GetUserDto> {
    const res = await fetch(getApiUrl('/api/user'), {
        credentials: 'include',
    });
   
    if (!res.ok) {
        throw new Error('Failed to fetch user')
    }
    return res.json()
}

export async function fetchUserSession(): Promise<GetUserSessionDto> {
    const res = await fetch(getApiUrl('/api/user/session'), {
        credentials: 'include',
    });
    if (!res.ok) {
        throw new Error('Failed to fetch user session')
    }
    return res.json()
}

export async function fetchUsers(): Promise<GetUsersResponseDto> {
    const res = await fetch(getApiUrl('/api/users'), {
        credentials: 'include',
    });
    
    if (!res.ok) {
        throw new Error('Failed to fetch users');
    }

    return res.json();
}

export async function updateUserRole(userId: number, data: UpdateUserRoleDto): Promise<UpdateUserRoleResponseDto> {
    const res = await fetch(getApiUrl(`/api/users/${userId}/role`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
    });
    
    if (!res.ok) {
        throw new Error('Failed to update user role');
    }
    
    return res.json();
}

export async function resendEmailVerification(userId: number): Promise<{ success: boolean; message: string }> {
    const res = await fetch(getApiUrl(`/api/users/${userId}/resend-verification`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });
    
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to resend verification email');
    }
    
    return res.json();
}

export async function deleteUser(userId: number): Promise<DeleteUserResponseDto> {
    const res = await fetch(getApiUrl(`/api/users/${userId}`), {
        method: 'DELETE',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to delete user');
    }

    return res.json();
}

export async function downloadUserReport(): Promise<void> {
    const res = await fetch(getApiUrl('/api/users/report'), {
        credentials: 'include',
    });
    
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to download user report');
    }
    
    // Get the CSV content
    const csvContent = await res.text();
    
    // Create a blob and download it
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user-report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export async function searchUsers(query: string, limit = 5): Promise<SearchUsersResponseDto> {
    const params = new URLSearchParams({
        q: query,
        limit: limit.toString(),
    });
    
    const res = await fetch(getApiUrl(`/api/users/search?${params}`), {
        credentials: 'include',
    });
    
    if (!res.ok) {
        throw new Error('Failed to search users');
    }
    
    return res.json();
}

export async function fetchUserUsageStats(): Promise<GetUserUsageStatsDto> {
    const res = await fetch(getApiUrl('/api/user/usage'), {
        credentials: 'include',
    });
    
    if (!res.ok) {
        throw new Error('Failed to fetch user usage stats');
    }
    
    return res.json();
}
